import { useCallback } from "react";

import type { SeatBasedTeamSubscription } from "../../../hooks/useTeamsTypes";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { type ModalProps } from "../../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { SubscriptionTier } from "../../../types";
import { SubscriptionUpgradeModal } from "../SubscriptionUpgradeModal/SubscriptionUpgradeModal";

export const UpgradeToStudioModal: React.FC<
  Pick<ModalProps, "open" | "onClose"> & {
    subscription: SeatBasedTeamSubscription;
    teamID: string | undefined;
  }
> = ({ subscription, teamID, onClose, open }) => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  const onTelemetryPaymentConfirmed = useCallback(() => {
    tracker.trackButtonClick({
      action: ElementClickAction.UPGRADE_MODAL_CONFIRMED,
      location: ElementClickLocation.TIER_UPGRADE_MODAL,
    });
  }, [tracker]);

  return (
    <SubscriptionUpgradeModal
      telemetryName="tier_upgrade"
      subscription={subscription}
      teamID={teamID}
      onClose={onClose}
      open={open}
      tier={SubscriptionTier.Studio}
      // TODO: multi-seat studio upgrade not yet supported — hiding selector, always sends null
      hideSeatSelector
      toApiSeatCount={(value) => (value === 1 ? null : value - 1)}
      copy={{
        title: t(m.UpgradeToStudioModal.Title),
        submitButton: t(m.UpgradeToStudioModal.ConfirmButton),
        toastSuccess: t(m.UpgradeToStudioModal.ToastSuccess),
        errorTitle: t(m.UpgradeToStudioModal.ErrorTitle),
        errorText: t(m.UpgradeToStudioModal.ErrorText),
        paymentErrorTitle: t(m.UpgradeToStudioModal.PaymentErrorTitle),
        paymentErrorText: t(m.UpgradeToStudioModal.PaymentErrorText),
        paymentCTAText: t(m.UpgradeToStudioModal.UpdateAndUpgradeCTA),
        learnMore: t(m.UpgradeToStudioModal.LearnMoreAboutTeams),
        tertiaryText: ({
          currencySymbol,
          proratedTotalAmount,
          renewalCurrencyCode,
          proratedCurrencyCode,
        }) =>
          t(m.UpgradeToStudioModal.TertiaryText, {
            currencySymbol,
            amount: proratedTotalAmount,
            allPricesInUSD:
              renewalCurrencyCode === "USD"
                ? t(m.Checkout.Summary.AllPricesIn, { currency: proratedCurrencyCode })
                : "",
          }),
      }}
      onTelemetryPaymentConfirmed={onTelemetryPaymentConfirmed}
    />
  );
};
